





































































































































































































































import { Component, Vue } from "vue-property-decorator";
import router from "@/router/router";
import MenuIconWithMultiSelectionListComponent from "@/components/common/MultiSelectionFilterMenu.vue";
import ContentStateComponent from "@/components/common/ContentState.vue";
import { ClientAdministrationRow, ClientFigures } from "@/data/models/ClientAdministrationRow";
import { getClientLoginToken, listClientsForAdministration } from "@/data/repository/ClientAdministrationRepository";
import { SnackbarModule } from "@/store/modules/SnackbarModule";
import { clientHandoverUrl, clientLoginErrorKey, newHandoverNonce, offerClientLoginToken } from "@/utils/ClientLoginHandover";
import { ToolbarContentModule } from "@/store/modules/ToolbarContent";
import {
  CLIENT_LIST_MULTI_FILTERS,
  clientListFilterGroup,
  clientListLastVisitedKey,
  clientListSelectionForLink,
  ClientListFilter,
  DEFAULT_CLIENT_LIST_SELECTION,
  isDefaultClientListSelection,
  orderedClientListSelection,
  parseClientListSelection,
  selectClientListRowsBySelection,
} from "@/utils/ClientAdministrationList";
import { EMPTY_VALUE, targetAppBuildText } from "@/utils/ClientAdministrationDetail";
import {
  CLIENT_LIST_COMPACT_FIGURE_COLUMNS,
  CLIENT_LIST_FIGURE_COLUMNS,
  CLIENT_LIST_FIGURE_GROUPS,
  clientFigureCellText,
  figuresUpdatedAtText,
  startsFigureGroup,
} from "@/utils/ClientListFigures";
import {
  COLOR_ALL_DEVICES_EDIT_ICON,
  COLOR_LIST_ROW_HOVER_DARK,
  COLOR_TABLE_HEADER,
  COLOR_TABLE_HEADER_DARK,
} from "@/utils/colors";
import { formatDateToDayMonthYear } from "@/utils/DateHelper";
import { ComponentName } from "@/utils/RouteGuards";
import { themed } from "@/utils/Theme";
import { formatNumber } from "@/utils/Utils";
import {
  getLastVisitedItem,
  LAST_VISITED_ITEM_BACKGROUND_STYLE,
  setLastVisitedItem,
  StorageKeyLastVisitedItemLevel,
} from "@/utils/Utils";
import { getValue, setValue } from "@/utils/SecureLocalStorage";
import { CLIENT_LIST_RETURN_PATH_KEY } from "@/utils/ClientAdministrationList";
import {
  applyQueuedClientRowUpdates,
  clearQueuedClientRowUpdates,
  hasQueuedClientRowUpdates,
  takeClientListReload,
} from "@/utils/ClientListRowUpdates";
import { Route } from "vue-router";
import { adminPortalChangeAccess } from "@/permissions/UserPermissions";
import { getReactiveActiveUser } from "@/data/repository/UserRepository";

/** Per-client session storage keys (see `SecureLocalStorage`) for the list's filter and search. */
// A JSON array of ticked filters. A new key, so a single filter stored before the multi-filter is
// simply ignored rather than misread.
const CLIENT_LIST_SELECTION_KEY = "ADMIN_CLIENTS_SELECTION";
const CLIENT_LIST_SEARCH_KEY = "ADMIN_CLIENTS_SEARCH";

/**
 * The admin portal's clients list, migrated from the IRES Admin Android app: the same filters, the
 * same counts, a search over name and client number.
 *
 * **Not the access boundary.** The route guard hides this screen from anyone without portal
 * membership and `MANAGE_CLIENTS`, but the data comes from the `listClientsForAdministration`
 * callable, which repeats both checks server-side and returns only the fields shown here.
 *
 * **Kept alive, like the other main lists** (`includeKeepAlive` in `Scaffold.vue`): coming back
 * from a client's detail reuses this instance, so the rows are not fetched again. Filter and search
 * therefore live in the component, never in the URL: the router view is keyed on the full path, so
 * every URL change would rebuild the screen and reload it from Cloud. They are also kept in
 * session storage, so a page refresh keeps them.
 *
 * A row opens the client's detail overview.
 */
@Component({
  name: "ClientAdministrationListComponent",
  components: {
    "multi-selection-menu-icon": MenuIconWithMultiSelectionListComponent,
    "content-state": ContentStateComponent,
  },
})
export default class ClientAdministrationListComponent extends Vue {
  clients: ClientAdministrationRow[] = [];
  /** When the figures were last counted (epoch ms); null before the first run. */
  figuresUpdatedAt: number | null = null;
  isLoading = true;
  loadError = false;
  searchQuery = "";
  /** The ticked filters; the default is "not deleted", the old "Alle". */
  selection: ClientListFilter[] = [...DEFAULT_CLIENT_LIST_SELECTION];
  /**
   * The last opened client's key, read once from session storage: Vue cannot track storage, and
   * reading it per row per render would hit storage for every row.
   */
  lastVisitedKey: string | null = null;
  /** Set when the user comes back from a client's detail, so the list scrolls to that client. */
  scrollToLastVisited = false;
  /** False while kept alive but not shown; gates the app bar portal. */
  componentActive = false;
  /** The client being opened as its IRES admin user (#2610069); the row buttons wait meanwhile. */
  openingClientId: string | null = null;

  readonly emptyValue = EMPTY_VALUE;
  readonly figureColumns = CLIENT_LIST_FIGURE_COLUMNS;
  readonly figureGroups = CLIENT_LIST_FIGURE_GROUPS;
  readonly compactFigureColumns = CLIENT_LIST_COMPACT_FIGURE_COLUMNS;

  beforeRouteEnter(_to: Route, from: Route, next: (callback: (vm: Vue) => void) => void) {
    next((vm) => {
      if (from.name === ComponentName.CLIENT_ADMINISTRATION_DETAIL) {
        (vm as ClientAdministrationListComponent).onBackFromDetail();
      }
    });
  }

  get multiFilterOptions(): ClientListFilter[] {
    return CLIENT_LIST_MULTI_FILTERS.map((entry) => entry.filter);
  }

  get defaultSelection(): ClientListFilter[] {
    return DEFAULT_CLIENT_LIST_SELECTION;
  }

  filterKey(filter: ClientListFilter): string {
    return filter;
  }

  filterGroupName(filter: ClientListFilter): string {
    return this.$t(`admin.clients.filterGroups.${clientListFilterGroup(filter)}`).toString();
  }

  /** The ticked filters in menu order, or nothing for the default selection. */
  get filterDescription(): string {
    if (isDefaultClientListSelection(this.selection)) {
      return "";
    }

    return orderedClientListSelection(this.selection).map((filter) => this.filterName(filter)).join(", ");
  }

  get tableHeaderColor() {
    return themed(COLOR_TABLE_HEADER, COLOR_TABLE_HEADER_DARK);
  }

  get rows(): ClientAdministrationRow[] {
    return selectClientListRowsBySelection(this.clients, this.selection, this.searchQuery);
  }

  /** The column headings show only above rows. */
  get hasRows(): boolean {
    return !this.isLoading && !this.loadError && this.rows.length > 0;
  }

  /** The two filters whose rows carry a value of their own, as in the Android list: one column each. */
  get infoColumns(): ClientListFilter[] {
    return [ClientListFilter.SERVICE_CONTRACT, ClientListFilter.HAS_APP].filter((filter) =>
      this.selection.includes(filter)
    );
  }

  /** The "Stand" in the header: the time alone today, with the date on another day. */
  get figuresUpdatedAtLabel(): string | null {
    return figuresUpdatedAtText(this.figuresUpdatedAt, new Date());
  }

  figureCell(client: ClientAdministrationRow, fields: ReadonlyArray<keyof ClientFigures>): string {
    return clientFigureCellText(client.figures, fields, formatNumber);
  }

  /** The phone's second line under the name: Kürzel and client number, whichever are recorded. */
  phoneSubtitle(client: ClientAdministrationRow): string {
    const parts = [client.iresProjectNumberPrefix, client.clientNumber].filter((part) => !!part);
    return parts.length > 0 ? parts.join(" · ") : EMPTY_VALUE;
  }

  startsGroup(index: number): boolean {
    return startsFigureGroup(index);
  }

  filterName(filter: ClientListFilter): string {
    return this.$t(`admin.clients.filters.${filter}`).toString();
  }

  /** Since when the service contract runs, or the target app build; "–" where it is not recorded. */
  infoFor(client: ClientAdministrationRow, info: ClientListFilter): string {
    if (info === ClientListFilter.SERVICE_CONTRACT) {
      const since = client.hasServiceContractForFirmwaresAndConfigsSince;
      const date = since !== null ? formatDateToDayMonthYear(new Date(since)) : null;

      return this.$t("admin.clients.serviceContractSince", [date ?? EMPTY_VALUE]).toString();
    }

    return targetAppBuildText(client.targetAppApk);
  }

  onSearchInput(value: string | null) {
    this.searchQuery = value ?? "";
    setValue(CLIENT_LIST_SEARCH_KEY, this.searchQuery);
  }

  /** Hover wins over the last-opened tint, so the row under the pointer always responds. */
  rowStyle(client: ClientAdministrationRow, hover: boolean): string {
    const hoverBackground = hover ? themed("", COLOR_LIST_ROW_HOVER_DARK) : "";

    if (hoverBackground) {
      return `background-color: ${hoverBackground}`;
    }

    return clientListLastVisitedKey(client.id) === this.lastVisitedKey
      ? LAST_VISITED_ITEM_BACKGROUND_STYLE
      : "";
  }

  onOpenClient(client: ClientAdministrationRow) {
    this.lastVisitedKey = clientListLastVisitedKey(client.id);
    setLastVisitedItem(this.lastVisitedKey, StorageKeyLastVisitedItemLevel.LEVEL_1);
    // The detail's Zurück returns to this exact address, so the kept-alive screen is found again
    // even when it was opened with a query (the cache is keyed on the full path).
    setValue(CLIENT_LIST_RETURN_PATH_KEY, this.$route.fullPath);

    router.push({ name: ComponentName.CLIENT_ADMINISTRATION_DETAIL, query: { clientId: client.id } });
  }

  get actionIconColor(): string {
    return COLOR_ALL_DEVICES_EDIT_ICON;
  }

  /** The shared IRES admin login cannot create a client either: IRES Cloud refuses it (#2610082). */
  get mayCreateClient(): boolean {
    return adminPortalChangeAccess(getReactiveActiveUser());
  }

  onCreateClient() {
    router.push({ name: ComponentName.CLIENT_ADMINISTRATION_SETUP });
  }

  /** The shared IRES admin login cannot open a client (#2610071): IRES Cloud refuses it. */
  get maySignInAsClient(): boolean {
    return adminPortalChangeAccess(getReactiveActiveUser());
  }

  get signInAsClientTooltip(): string {
    return this.$t(
      this.maySignInAsClient ? "admin.clients.clientLogin.button" : "admin.clients.clientLogin.refusals.SHARED_LOGIN"
    ).toString();
  }

  /**
   * "Als Mandant anmelden" (#2610069): opens the new tab at once, inside the click (a tab opened after
   * waiting for IRES Cloud is blocked as a pop-up), with `noopener`, then fetches the token and hands it
   * to that tab (`ClientLoginHandover.ts`). A client that cannot be opened is said so here; IRES Cloud
   * records each opening in the client's Verlauf.
   */
  async onSignInAsClient(client: ClientAdministrationRow) {
    const nonce = newHandoverNonce();
    window.open(clientHandoverUrl(window.location, nonce), "_blank", "noopener");

    this.openingClientId = client.id;
    try {
      const outcome = await offerClientLoginToken(nonce, getClientLoginToken(client.id));
      if (outcome === "NO_TAB") {
        SnackbarModule.showError({ key: "admin.clients.clientLogin.noTab" });
      }
    } catch (error) {
      SnackbarModule.showError({ key: clientLoginErrorKey(error) });
    } finally {
      this.openingClientId = null;
    }
  }

  onSelectionChanged(selection: ClientListFilter[]) {
    this.selection = selection;
    setValue(CLIENT_LIST_SELECTION_KEY, JSON.stringify(selection));
  }

  /** Scroll to the client just viewed once its row exists: now if it does, else after the load. */
  onBackFromDetail() {
    this.scrollToLastVisited = true;

    if (!this.isLoading) {
      this.scrollToLastVisitedClient();
    }
  }

  /**
   * The rows again without the loading state, after a client was created (#2610082): the list stays as it
   * is meanwhile, and a failure keeps it, since Aktualisieren is still there.
   */
  async reloadInBackground() {
    try {
      const list = await listClientsForAdministration();
      clearQueuedClientRowUpdates();
      this.clients = list.clients;
      this.figuresUpdatedAt = list.figuresUpdatedAt;
    } catch {
      // Swallowed on purpose: the rows shown stay valid, and Aktualisieren retries.
    }
  }

  async load() {
    this.isLoading = true;
    this.loadError = false;

    try {
      const list = await listClientsForAdministration();
      // Fresh rows already carry whatever a detail changed.
      clearQueuedClientRowUpdates();
      this.clients = list.clients;
      this.figuresUpdatedAt = list.figuresUpdatedAt;
      this.isLoading = false;
      this.scrollToLastVisitedClient();
    } catch {
      // Swallowed on purpose: the screen shows its own failure state (a refusal from Cloud included),
      // and the refresh button is the only useful action.
      this.loadError = true;
      this.clients = [];
    } finally {
      this.isLoading = false;
    }
  }

  /**
   * Back from a client's detail, bring that client into view. The router does not: the in-page back
   * button is a new navigation, which the router answers by scrolling to the top, and after a
   * refresh the rows arrive from Cloud well after it would have restored anything. Deferred by one
   * macrotask so it runs after the router's own scroll.
   */
  async scrollToLastVisitedClient() {
    if (!this.scrollToLastVisited || !this.lastVisitedKey) {
      return;
    }

    this.scrollToLastVisited = false;
    await this.$nextTick();
    await new Promise((resolve) => setTimeout(resolve, 0));

    const row = Array.from(this.$el.querySelectorAll<HTMLElement>("[data-client-id]")).find(
      (element) => clientListLastVisitedKey(element.dataset.clientId ?? "") === this.lastVisitedKey
    );

    row?.scrollIntoView({ block: "center" });
  }

  mounted() {
    this.componentActive = true;
    ToolbarContentModule.setI18nResource("admin.clients.title");
    this.lastVisitedKey = getLastVisitedItem(StorageKeyLastVisitedItemLevel.LEVEL_1);
    this.searchQuery = getValue(CLIENT_LIST_SEARCH_KEY) ?? "";

    // A link may ask for filters (the IRES Admin cockpit's figures do); they become the selection
    // that shows exactly the clients the figure counted, and it is stored. Read once, here:
    // later changes stay out of the URL (see above).
    const linkedSelection = clientListSelectionForLink(this.$route.query.filter);
    if (linkedSelection) {
      this.onSelectionChanged(linkedSelection);
    } else {
      this.selection = parseClientListSelection(getValue(CLIENT_LIST_SELECTION_KEY));
    }

    this.load();
  }

  /**
   * Back from the cache: the rows are still there, only the app bar and the tint need refreshing, and
   * any row a client's detail changed meanwhile ("Kein Echtbetrieb") is updated in place.
   */
  activated() {
    this.componentActive = true;
    if (takeClientListReload()) {
      this.reloadInBackground();
    } else if (hasQueuedClientRowUpdates()) {
      this.clients = applyQueuedClientRowUpdates(this.clients);
    }
    ToolbarContentModule.setI18nResource("admin.clients.title");
    this.lastVisitedKey = getLastVisitedItem(StorageKeyLastVisitedItemLevel.LEVEL_1);
  }

  deactivated() {
    this.componentActive = false;
  }
}
